import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  IconButton,
  Divider,
  Typography,
  Button,
  Box,
  Snackbar,
  Alert,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { createItemType, updateItemType } from "../../services/InventorySettings";
import { IItemTypeRequestDTO } from "../../interfaces/InventorySettings";
import { formFieldStyles } from "../../styles/formStyles";
import { useAuth } from "../../auth/AuthProvider";

interface AddEditTypeProps {
  open: boolean;
  onClose: () => void;
  editData: any;
  onSuccess: (message: string) => void;
}

const AddEditType: React.FC<AddEditTypeProps> = ({
  open,
  onClose,
  editData,
  onSuccess,
}) => {
  const [formData, setFormData] = useState({
    typeName: "",
    categoryName: "",
  });
  const [errors, setErrors] = useState<any>({});
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState<"success" | "error">("success");
  const { getLoginUserInfo } = useAuth();
  const loggedInUser = getLoginUserInfo();
  const COMPANY_ID = loggedInUser?.companyId;

  useEffect(() => {
    if (open) {
      if (editData) {
        setFormData({
          typeName: editData.typeName || "",
          categoryName: editData.categoryName || "",
        });
      } else {
        setFormData({
          typeName: "",
          categoryName: "",
        });
      }
      setErrors({});
    }
  }, [editData, open]);

  const validate = () => {
    const newErrors: any = {};
    if (!formData.typeName) newErrors.typeName = "Type name is required";
    if (!editData && !formData.categoryName) newErrors.categoryName = "Category name is required";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;
    if (!COMPANY_ID) {
      setSnackbarMessage("User information not available");
      setSnackbarSeverity("error");
      setShowSnackbar(true);
      return;
    }

    try {
      const isEdit = Boolean(editData?.id);
      
      const dto: IItemTypeRequestDTO = isEdit 
        ? {
            companyId: COMPANY_ID,
            typeName: formData.typeName,
            categoryName: "", // Not used in edit mode
          }
        : {
            companyId: COMPANY_ID,
            typeName: formData.typeName,
            categoryName: formData.categoryName,
          };

      const response = isEdit 
        ? await updateItemType(editData.id, dto)
        : await createItemType(dto);

      if (response && response.status === "success") {
        const message = isEdit ? "Type updated successfully" : "Type created successfully";
        onSuccess(message);
        onClose();
      } else {
        const errorMsg = response?.errorResponse?.response?.data?.message || response?.errorResponse?.message || "Operation failed";
        throw new Error(errorMsg);
      }
    } catch (err: any) {
      console.error("Save error:", err);
      setSnackbarMessage(err?.message || "An error occurred while saving");
      setSnackbarSeverity("error");
      setShowSnackbar(true);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" PaperProps={{ sx: { borderRadius: "12px", width: "600px" } }} slotProps={{ backdrop: { sx: { backdropFilter: "blur(4px)" } } }}>
      <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", pb: 0.5, pt: 1.5, px: 2 }}>
        <Box>
          <Typography sx={{ fontSize: "20px", fontWeight: 600 }}>{editData ? "Edit Type" : "Add Type"}</Typography>
          <Typography variant="body2" color="grey.500" sx={{ fontSize: "12px", mt: 0.3 }}>
            {editData ? "Edit Type" : "Add Type"}
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent sx={{ px: 2, py: 1.5 }}>
        <Grid container spacing={1.5} sx={{ mt: 0 }}>
          <Grid item xs={editData?12:6}>
            <Typography sx={{ mb: 0.3, fontSize: "14px", fontWeight: 500 }}>Type Name <span style={{ color: "red" }}>*</span></Typography>
            <TextField
              fullWidth
              size="small"
              placeholder="eg. Electronics"
              value={formData.typeName}
              onChange={(e) => setFormData({ ...formData, typeName: e.target.value })}
              error={!!errors.typeName}
              helperText={errors.typeName}
              sx={{...formFieldStyles}}
            />
          </Grid>

          {!editData && (
            <Grid item xs={6}>
              <Typography sx={{ mb: 0.3, fontSize: "14px", fontWeight: 500 }}>Category Name <span style={{ color: "red" }}>*</span></Typography>
              <TextField
                fullWidth
                size="small"
                placeholder="eg. Mobile Phones"
                value={formData.categoryName}
                onChange={(e) => setFormData({ ...formData, categoryName: e.target.value })}
                error={!!errors.categoryName}
                helperText={errors.categoryName}
                sx={{...formFieldStyles}}
              />
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <Divider sx={{ml:2, mr:2}}/>
      <DialogActions sx={{ ml:1, mr:1, justifyContent: "space-between" }}>
        <Button onClick={onClose} variant="outlined" sx={{ borderRadius: "6px", border: "1px solid", borderColor: "primary.main", boxShadow: "0 0 10.7px 0 rgba(0, 94, 255, 0.20) inset", padding: "8px 16px", textTransform: "none" }}>Cancel</Button>
        <Button onClick={handleSave} variant="contained" sx={{ borderRadius: "6px", border: "1px solid", borderColor: "primary.main", boxShadow: "0 0 10.7px 0 rgba(0, 0, 0, 0.25) inset", padding: "8px 40px", textTransform: "none" }}>{editData ? "Save" : "Add"}</Button>
      </DialogActions>

      <Snackbar 
        open={showSnackbar} 
        autoHideDuration={2000}
        onClose={() => setShowSnackbar(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={() => setShowSnackbar(false)} 
          severity={snackbarSeverity}
          variant="filled"
          sx={{ 
            width: "100%", 
            backgroundColor: `${snackbarSeverity === "success" ? "success.main" : "error.main"} !important`,
            color: "white",
            "& .MuiAlert-icon": { color: "white" },
            "& .MuiAlert-action": { color: "white" }
          }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Dialog>
  );
};

export default AddEditType;